import { useState, useCallback, useEffect, useMemo } from "react";
import {
  ChevronLeft,
  Plus,
  Trash2,
  Check,
  Flame,
  Gift,
  Star,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eyebrow, Wordmark } from "@/components/editorial";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { useFoodExclusions } from "@/features/food-exclusions/useFoodExclusions";
import {
  useHouseholdProfile,
  type HouseholdProfilePayload,
} from "@/features/household/useHouseholdProfile";
import { useStreak } from "@/features/streak/useStreak";
import { useAmbassador } from "@/features/ambassador/useAmbassador";
import {
  ALLERGY_DECK,
  APPETITE,
  DIETS,
  EQUIPMENT_OPTIONS,
  EXPERTISE,
  WEEKDAY_LABELS_LONG,
  WEEKDAY_LABELS_SHORT,
} from "@/features/onboarding/constants";
import { Slider } from "@/components/ui/slider";
import { OnboardingIcon } from "@/components/onboarding-icons/OnboardingIcons";
import { AccountSection } from "@/features/auth/AccountSection";

export function ProfilePage() {
  const { profile, isLoading, isError, isMissing, refetch, update } =
    useHouseholdProfile();

  return (
    <div className="flex h-full flex-col bg-background pb-2">
      {/* Header */}
      <header className="flex items-center justify-between px-5 pt-4">
        <Link
          to="/dashboard"
          aria-label="Retour"
          className="flex h-9 w-9 items-center justify-center text-foreground"
        >
          <ChevronLeft className="size-5" strokeWidth={1.25} />
        </Link>
        <Wordmark size="sm" />
        <div className="w-9" />
      </header>

      <div className="flex-1 overflow-auto px-5 pb-6">
        <section className="mb-2 pt-2">
          <Eyebrow className="mb-1.5">Options</Eyebrow>
          <h1 className="h-display text-[32px] leading-[1]">Vos préférences</h1>
        </section>

        <StreakSection />
        <AmbassadorSection />
        <FoodExclusionsSection />

        {isLoading && <HouseholdSkeleton />}

        {isError && !isLoading && <HouseholdErrorState onRetry={refetch} />}

        {isMissing && !isLoading && <HouseholdMissingState />}

        {profile && !isLoading && (
          <>
            <HouseholdSizeSection profile={profile} update={update} />
            <HouseholdDietSection profile={profile} update={update} />
            <HouseholdAllergiesSection profile={profile} update={update} />
            <HouseholdEquipmentSection profile={profile} update={update} />
            <HouseholdExpertiseSection profile={profile} update={update} />
            <HouseholdAppetiteSection profile={profile} update={update} />
            <HouseholdBatchScheduleSection profile={profile} update={update} />
            {(profile.diet === "omnivore" ||
              profile.diet === "pescatarian") && (
              <HouseholdMeatDaysSection profile={profile} update={update} />
            )}
          </>
        )}

        <AccountSection />
      </div>
    </div>
  );
}

// ─── Shared section props ────────────────────────────────────────────────────

type UpdateMutation = ReturnType<typeof useHouseholdProfile>["update"];

interface SectionProps {
  profile: HouseholdProfilePayload;
  update: UpdateMutation;
}

// ─── Section shell ────────────────────────────────────────────────────────────

function SectionCard({
  title,
  subtitle,
  children,
  isDirty,
  isSaving,
  onSave,
}: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
  isDirty: boolean;
  isSaving: boolean;
  onSave: () => void;
}) {
  const { t } = useTranslation();
  return (
    <section className="mt-5 overflow-hidden border border-hair bg-background">
      <header className="flex items-center justify-between border-b border-hair bg-paper px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-[18px] italic tracking-[-0.005em]">
            {title}
          </h2>
          <p className="mt-0.5 pr-2 text-[12px] leading-snug text-ink-50">
            {subtitle}
          </p>
        </div>
        {isDirty && (
          <Button
            variant="accent"
            size="sm"
            onClick={onSave}
            disabled={isSaving}
            className="shrink-0"
          >
            {isSaving ? (
              <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            ) : (
              <Check className="size-3.5" />
            )}
            {t("household.save")}
          </Button>
        )}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

// ─── Section 1: Household size ────────────────────────────────────────────────

function HouseholdSizeSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [size, setSize] = useState(profile.size);

  useEffect(() => {
    setSize(profile.size);
  }, [profile.size]);

  const isDirty = size !== profile.size;

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, size });
  }, [update, profile, size]);

  return (
    <SectionCard
      title={t("household.size.title")}
      subtitle={t("household.size.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setSize((s) => Math.max(1, s - 1))}
            aria-label="Moins une personne"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-hair text-lg hover:border-ink-30 disabled:opacity-30"
            disabled={size <= 1}
          >
            −
          </button>
          <span className="w-8 text-center font-serif text-[28px] font-light tabular-nums">
            {size}
          </span>
          <button
            type="button"
            onClick={() => setSize((s) => Math.min(12, s + 1))}
            aria-label="Plus une personne"
            className="flex h-9 w-9 items-center justify-center rounded-full border border-hair text-lg hover:border-ink-30 disabled:opacity-30"
            disabled={size >= 12}
          >
            +
          </button>
        </div>
        <span className="font-serif text-[15px] text-ink-50">
          {size === 1
            ? t("household.size.person")
            : t("household.size.persons")}
        </span>
      </div>
    </SectionCard>
  );
}

// ─── Section 2: Diet ──────────────────────────────────────────────────────────

function HouseholdDietSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [diet, setDiet] = useState(profile.diet);

  useEffect(() => {
    setDiet(profile.diet);
  }, [profile.diet]);

  const isDirty = diet !== profile.diet;

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, diet });
  }, [update, profile, diet]);

  return (
    <SectionCard
      title={t("household.diet.title")}
      subtitle={t("household.diet.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-col gap-2">
        {DIETS.map((d) => (
          <ProfileRowCard
            key={d.id}
            label={d.label}
            sub={d.hint}
            icon={d.icon}
            selected={diet === d.id}
            onClick={() => setDiet(d.id)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Section 3: Allergies ─────────────────────────────────────────────────────

function HouseholdAllergiesSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [allergies, setAllergies] = useState<string[]>(profile.allergies);

  useEffect(() => {
    setAllergies(profile.allergies);
  }, [profile.allergies]);

  const isDirty =
    JSON.stringify([...allergies].sort()) !==
    JSON.stringify([...profile.allergies].sort());

  const toggle = (id: string) => {
    setAllergies((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, allergies });
  }, [update, profile, allergies]);

  return (
    <SectionCard
      title={t("household.allergies.title")}
      subtitle={t("household.allergies.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-wrap gap-2">
        {ALLERGY_DECK.map((a) => (
          <ProfileChip
            key={a.id}
            label={a.label}
            icon={a.icon}
            selected={allergies.includes(a.id)}
            onClick={() => toggle(a.id)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Section 4: Equipment ─────────────────────────────────────────────────────

function HouseholdEquipmentSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [equipment, setEquipment] = useState<string[]>(profile.equipment);

  useEffect(() => {
    setEquipment(profile.equipment);
  }, [profile.equipment]);

  const isDirty =
    JSON.stringify([...equipment].sort()) !==
    JSON.stringify([...profile.equipment].sort());

  const toggle = (id: string) => {
    setEquipment((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id],
    );
  };

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, equipment });
  }, [update, profile, equipment]);

  return (
    <SectionCard
      title={t("household.equipment.title")}
      subtitle={t("household.equipment.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-wrap gap-2">
        {EQUIPMENT_OPTIONS.map((e) => (
          <ProfileChip
            key={e.id}
            label={e.label}
            icon={e.icon}
            selected={equipment.includes(e.id)}
            onClick={() => toggle(e.id)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Section 5: Expertise ─────────────────────────────────────────────────────

function HouseholdExpertiseSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [expertise, setExpertise] = useState(profile.expertise);

  useEffect(() => {
    setExpertise(profile.expertise);
  }, [profile.expertise]);

  const isDirty = expertise !== profile.expertise;

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, expertise });
  }, [update, profile, expertise]);

  return (
    <SectionCard
      title={t("household.expertise.title")}
      subtitle={t("household.expertise.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-col gap-2">
        {EXPERTISE.map((e) => (
          <ProfileRowCard
            key={e.id}
            label={e.label}
            sub={e.hint}
            icon={e.icon}
            selected={expertise === e.id}
            onClick={() => setExpertise(e.id)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Section 6: Appetite ──────────────────────────────────────────────────────

function HouseholdAppetiteSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [appetite, setAppetite] = useState(profile.appetite);

  useEffect(() => {
    setAppetite(profile.appetite);
  }, [profile.appetite]);

  const isDirty = appetite !== profile.appetite;

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, appetite });
  }, [update, profile, appetite]);

  return (
    <SectionCard
      title={t("household.appetite.title")}
      subtitle={t("household.appetite.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-col gap-2">
        {APPETITE.map((a) => (
          <ProfileRowCard
            key={a.id}
            label={a.label}
            sub={a.hint}
            selected={appetite === a.id}
            onClick={() => setAppetite(a.id)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

// ─── Section 7: Batch schedule ───────────────────────────────────────────────

function HouseholdBatchScheduleSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const [batchStartDay, setBatchStartDay] = useState(
    profile.batch_start_day ?? 0,
  );
  const [batchNumDays, setBatchNumDays] = useState(profile.batch_num_days ?? 7);
  const [cookingDay, setCookingDay] = useState(profile.cooking_day ?? 6);

  useEffect(() => {
    setBatchStartDay(profile.batch_start_day ?? 0);
    setBatchNumDays(profile.batch_num_days ?? 7);
    setCookingDay(profile.cooking_day ?? 6);
  }, [profile.batch_start_day, profile.batch_num_days, profile.cooking_day]);

  const isDirty =
    batchStartDay !== (profile.batch_start_day ?? 0) ||
    batchNumDays !== (profile.batch_num_days ?? 7) ||
    cookingDay !== (profile.cooking_day ?? 6);

  const handleSave = useCallback(() => {
    update.mutate({
      ...profile,
      batch_start_day: batchStartDay,
      batch_num_days: batchNumDays,
      cooking_day: cookingDay,
    });
  }, [update, profile, batchStartDay, batchNumDays, cookingDay]);

  const selectedDays = useMemo(
    () =>
      Array.from({ length: batchNumDays }, (_, i) => (batchStartDay + i) % 7),
    [batchStartDay, batchNumDays],
  );

  const handleDayToggle = (dayIdx: number) => {
    const end = (batchStartDay + batchNumDays - 1) % 7;
    if (selectedDays.includes(dayIdx)) {
      if (dayIdx === batchStartDay && batchNumDays > 1) {
        setBatchStartDay((batchStartDay + 1) % 7);
        setBatchNumDays(batchNumDays - 1);
      } else if (dayIdx === end && batchNumDays > 1) {
        setBatchNumDays(batchNumDays - 1);
      }
    } else {
      const prevEnd = (dayIdx - 1 + 7) % 7;
      const nextStart = (dayIdx + 1) % 7;
      if (prevEnd === end && batchNumDays < 7) {
        setBatchNumDays(batchNumDays + 1);
      } else if (nextStart === batchStartDay && batchNumDays < 7) {
        setBatchStartDay(dayIdx);
        setBatchNumDays(batchNumDays + 1);
      } else {
        setBatchStartDay(dayIdx);
        setBatchNumDays(1);
      }
    }
  };

  return (
    <SectionCard
      title={t("household.batch_schedule.title")}
      subtitle={t("household.batch_schedule.subtitle")}
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-col gap-5">
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-ink-50">
            Jours batch
          </p>
          <div className="flex gap-1.5">
            {WEEKDAY_LABELS_SHORT.map((label, idx) => {
              const isSelected = selectedDays.includes(idx);
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleDayToggle(idx)}
                  aria-pressed={isSelected}
                  className={cn(
                    "flex flex-1 flex-col items-center rounded-sm border py-2.5 transition-colors",
                    isSelected
                      ? "border-foreground bg-foreground text-background"
                      : "border-hair bg-background text-foreground hover:border-ink-30",
                  )}
                >
                  <span className="text-[10px] uppercase tracking-[0.12em]">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[12px] text-ink-50">
            {batchNumDays} dîner{batchNumDays > 1 ? "s" : ""} / semaine
          </p>
        </div>
        <div>
          <p className="mb-2 text-[11px] uppercase tracking-[0.18em] text-ink-50">
            {t("household.batch_schedule.cooking_day_label")}
          </p>
          <div className="flex gap-1.5">
            {WEEKDAY_LABELS_SHORT.map((label, idx) => {
              const isSelected = cookingDay === idx;
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCookingDay(idx)}
                  aria-pressed={isSelected}
                  className={cn(
                    "flex flex-1 flex-col items-center rounded-sm border py-2.5 transition-colors",
                    isSelected
                      ? "border-accent bg-accent/10 text-accent"
                      : "border-hair bg-background text-foreground hover:border-ink-30",
                  )}
                >
                  <span className="text-[10px] uppercase tracking-[0.12em]">
                    {label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[12px] text-ink-50">
            {t("household.batch_schedule.recap", {
              count: batchNumDays,
              day: WEEKDAY_LABELS_LONG[cookingDay],
            })}
          </p>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Section 8: Meat days ─────────────────────────────────────────────────────

function HouseholdMeatDaysSection({ profile, update }: SectionProps) {
  const { t } = useTranslation();
  const isPescatarian = profile.diet === "pescatarian";
  const batchNumDays = profile.batch_num_days ?? 7;
  const [meatDays, setMeatDays] = useState<number | null>(
    profile.meat_days_per_week ?? null,
  );

  useEffect(() => {
    setMeatDays(profile.meat_days_per_week ?? null);
  }, [profile.meat_days_per_week]);

  const isDirty = meatDays !== (profile.meat_days_per_week ?? null);

  const handleSave = useCallback(() => {
    update.mutate({ ...profile, meat_days_per_week: meatDays });
  }, [update, profile, meatDays]);

  const sliderValue = meatDays ?? batchNumDays;

  return (
    <SectionCard
      title={
        isPescatarian
          ? t("household.meat_days.title_pescatarian")
          : t("household.meat_days.title")
      }
      subtitle={
        isPescatarian
          ? t("household.meat_days.subtitle_pescatarian")
          : t("household.meat_days.subtitle")
      }
      isDirty={isDirty}
      isSaving={update.isPending}
      onSave={handleSave}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="font-serif text-[56px] font-light leading-none tabular-nums">
          {meatDays === null ? "∞" : meatDays}
        </div>
        <p className="text-[13px] text-ink-50">
          {meatDays === null
            ? t("household.meat_days.unlimited")
            : `${meatDays} / ${batchNumDays} dîners`}
        </p>
        <Slider
          value={useMemo(() => [sliderValue], [sliderValue])}
          min={0}
          max={batchNumDays}
          step={1}
          onValueChange={(v) => {
            const val = v[0] ?? 0;
            setMeatDays(val === batchNumDays ? null : val);
          }}
          className="w-full"
          aria-label={
            isPescatarian
              ? t("household.meat_days.title_pescatarian")
              : t("household.meat_days.title")
          }
        />
      </div>
    </SectionCard>
  );
}

// ─── Loading / error states ───────────────────────────────────────────────────

function HouseholdSkeleton() {
  return (
    <>
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="mt-5 border border-hair bg-background"
          aria-hidden="true"
        >
          <div className="border-b border-hair bg-paper px-5 py-4">
            <div className="h-4 w-40 animate-pulse rounded bg-ink-10" />
            <div className="mt-2 h-3 w-64 animate-pulse rounded bg-ink-10" />
          </div>
          <div className="px-5 py-4">
            <div className="h-10 w-full animate-pulse rounded bg-ink-10" />
          </div>
        </div>
      ))}
    </>
  );
}

function HouseholdMissingState() {
  const { t } = useTranslation();
  return (
    <div className="mt-5 border border-hair bg-background px-5 py-8 text-center">
      <p className="text-[13px] text-ink-50">{t("household.not_configured")}</p>
      <Button asChild size="sm" className="mt-3">
        <Link to="/onboarding">{t("household.start_onboarding")}</Link>
      </Button>
    </div>
  );
}

function HouseholdErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mt-5 border border-hair bg-background px-5 py-8 text-center">
      <p className="text-[13px] text-ink-50">{t("household.error_load")}</p>
      <Button variant="ghost" size="sm" className="mt-3" onClick={onRetry}>
        {t("common.retry")}
      </Button>
    </div>
  );
}

// ─── Primitive controls (local variants, not bound to onboarding store) ───────

type IconName = Parameters<typeof OnboardingIcon>[0]["name"];

function ProfileRowCard({
  label,
  sub,
  icon,
  selected,
  onClick,
}: {
  label: string;
  sub?: string;
  icon?: IconName;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "flex w-full items-center gap-4 border bg-background px-5 py-4 text-left transition-colors",
        selected ? "border-foreground" : "border-hair hover:border-ink-30",
      )}
    >
      {icon ? (
        <div className="flex size-10 shrink-0 items-center justify-center text-ink">
          <OnboardingIcon name={icon} className="size-8" />
        </div>
      ) : (
        <span
          className={cn(
            "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
            selected ? "border-foreground" : "border-ink-30",
          )}
        >
          {selected && <span className="h-2 w-2 rounded-full bg-foreground" />}
        </span>
      )}
      <span className="flex-1">
        <span className="block font-serif text-[15px] font-medium leading-tight">
          {label}
        </span>
        {sub && (
          <span className="mt-0.5 block text-[12px] text-ink-50">{sub}</span>
        )}
      </span>
    </button>
  );
}

function ProfileChip({
  label,
  icon,
  selected,
  onClick,
}: {
  label: string;
  icon?: IconName;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-[12.5px] font-medium transition-colors",
        selected
          ? "border-foreground bg-foreground text-background"
          : "border-hair bg-background text-ink-70 hover:border-ink-30",
      )}
    >
      {icon && (
        <OnboardingIcon
          name={icon}
          className={cn("size-5", selected ? "text-background" : "text-ink")}
        />
      )}
      {selected && <Check className="size-3" />}
      {label}
    </button>
  );
}

// ─── Streak ─────────────────────────────────────────────────────────────────

function StreakSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useStreak();

  if (isLoading) return null;

  return (
    <section className="mb-6">
      <Eyebrow className="mb-2">{t("streak.title")}</Eyebrow>
      <div className="rounded-sm border border-hair p-4">
        {data.current_streak === 0 ? (
          <p className="text-[13px] text-ink-50">{t("streak.no_streak")}</p>
        ) : (
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Flame className="size-5 text-orange-500" strokeWidth={1.5} />
              <div>
                <p className="font-mono text-[22px] font-medium leading-none">
                  {data.current_streak}
                </p>
                <p className="mt-0.5 text-[11px] text-ink-50">
                  {t("streak.current")}
                </p>
              </div>
            </div>
            <div className="h-8 w-px bg-hair" />
            <div>
              <p className="font-mono text-[18px] font-medium leading-none text-ink-50">
                {data.longest_streak}
              </p>
              <p className="mt-0.5 text-[11px] text-ink-50">
                {t("streak.longest")}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Ambassador ──────────────────────────────────────────────────────────────

const TIER_THRESHOLDS = [1, 3, 5] as const;

function AmbassadorSection() {
  const { t } = useTranslation();
  const { data, isLoading } = useAmbassador();

  if (isLoading) return null;

  const {
    referrals_validated,
    theme_unlocked,
    editorial_weeks,
    promo_code,
    next_tier_at,
  } = data;
  const maxTier = next_tier_at === null;
  const progressTarget =
    next_tier_at ?? TIER_THRESHOLDS[TIER_THRESHOLDS.length - 1];

  return (
    <section className="mb-6">
      <Eyebrow className="mb-2">{t("ambassador.title")}</Eyebrow>
      <div className="rounded-sm border border-hair p-4 space-y-4">
        {/* Progress bar */}
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[12px] text-ink-50">
              {t("ambassador.referrals_validated")}
            </span>
            <span className="font-mono text-[12px]">
              {t("ambassador.progress_label", {
                current: referrals_validated,
                target: progressTarget,
              })}
            </span>
          </div>
          <Progress value={referrals_validated} max={progressTarget} />
          {maxTier ? (
            <p className="mt-1.5 text-[11px] text-ink-50">
              {t("ambassador.max_tier")}
            </p>
          ) : (
            <p className="mt-1.5 text-[11px] text-ink-50">
              {t("ambassador.next_tier", {
                count: next_tier_at - referrals_validated,
              })}
            </p>
          )}
        </div>

        {/* Tier badges */}
        <div className="space-y-2">
          <TierRow
            unlocked={theme_unlocked}
            icon={<Star className="size-3.5" />}
            label={t("ambassador.tier_1_label")}
            desc={t("ambassador.tier_1_desc")}
          />
          <TierRow
            unlocked={editorial_weeks}
            icon={<Gift className="size-3.5" />}
            label={t("ambassador.tier_3_label")}
            desc={t("ambassador.tier_3_desc")}
          />
          <TierRow
            unlocked={promo_code !== null}
            icon={<Gift className="size-3.5" />}
            label={t("ambassador.tier_5_label")}
            desc={t("ambassador.tier_5_desc")}
          />
        </div>

        {/* Promo code */}
        {promo_code && (
          <div className="rounded-sm bg-hair/40 px-3 py-2">
            <p className="mb-1 text-[11px] uppercase tracking-[0.1em] text-ink-50">
              {t("ambassador.promo_code_label")}
            </p>
            <p className="font-mono text-[15px] font-medium tracking-wider">
              {promo_code}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function TierRow({
  unlocked,
  icon,
  label,
  desc,
}: {
  unlocked: boolean;
  icon: React.ReactNode;
  label: string;
  desc: string;
}) {
  return (
    <div className={cn("flex items-start gap-2.5", !unlocked && "opacity-40")}>
      <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full border border-hair">
        {icon}
      </span>
      <div>
        <p className="text-[13px] font-medium leading-tight">{label}</p>
        <p className="mt-0.5 text-[11px] text-ink-50">{desc}</p>
      </div>
      {unlocked && (
        <Badge variant="accent" className="ml-auto shrink-0">
          ✓
        </Badge>
      )}
    </div>
  );
}

// ─── Food exclusions (existing, unchanged) ────────────────────────────────────

function FoodExclusionsSection() {
  const { t } = useTranslation();
  const { exclusions, isLoading, isError, add, remove, refetch } =
    useFoodExclusions();
  const [inputValue, setInputValue] = useState("");

  const handleAdd = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    add.mutate(trimmed);
    setInputValue("");
  }, [inputValue, add]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleAdd();
      }
    },
    [handleAdd],
  );

  return (
    <section className="mt-5 overflow-hidden border border-hair bg-background">
      {/* Section header */}
      <header className="flex items-center justify-between border-b border-hair bg-paper px-5 py-4">
        <div className="min-w-0 flex-1">
          <h2 className="font-serif text-[18px] italic tracking-[-0.005em]">
            {t("food_exclusions.title")}
          </h2>
          <p className="mt-0.5 pr-2 text-[12px] leading-snug text-ink-50">
            {t("food_exclusions.subtitle")}
          </p>
        </div>
        <div className="shrink-0 text-right text-[11px] text-ink-30">
          {exclusions.length}/50
        </div>
      </header>

      {/* Add form */}
      <div className="flex items-end gap-2 border-b border-hair px-5 py-4">
        <div className="flex-1">
          <Input
            placeholder={t("food_exclusions.add_placeholder")}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={add.isPending}
            aria-label={t("food_exclusions.add_placeholder")}
          />
        </div>
        <Button
          variant="accent"
          size="sm"
          onClick={handleAdd}
          disabled={
            add.isPending || !inputValue.trim() || exclusions.length >= 50
          }
          aria-label={t("food_exclusions.add")}
        >
          {add.isPending ? (
            <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Plus className="size-4" strokeWidth={2} />
          )}
          {t("food_exclusions.add")}
        </Button>
      </div>

      {/* List body */}
      <div className="min-h-[120px]">
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
          </div>
        )}

        {isError && !isLoading && (
          <div className="px-5 py-6 text-center">
            <p className="text-[13px] text-ink-50">
              {t("food_exclusions.error_list")}
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-3"
              onClick={() => refetch()}
            >
              {t("common.retry")}
            </Button>
          </div>
        )}

        {!isLoading && !isError && exclusions.length === 0 && (
          <p className="px-5 py-8 text-center text-[13px] text-ink-50">
            {t("food_exclusions.empty")}
          </p>
        )}

        {!isLoading && !isError && exclusions.length > 0 && (
          <ul
            className="divide-y divide-hair"
            aria-label={t("food_exclusions.title")}
          >
            {exclusions.map((item) => (
              <li key={item}>
                <div className="flex items-center gap-3 px-5 py-3">
                  <span className="min-w-0 flex-1 font-serif text-[15px] leading-tight">
                    {item}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-ink-50 hover:text-destructive"
                    onClick={() => remove.mutate(item)}
                    disabled={remove.isPending}
                    aria-label={`${t("food_exclusions.remove")} ${item}`}
                  >
                    {remove.isPending ? (
                      <span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    ) : (
                      <Trash2 className="size-[16px]" strokeWidth={1.5} />
                    )}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
